import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Save, Upload, X } from 'lucide-react';
import { businessService, type Business } from '@/services/supabase/business.service';
import { storageService } from '@/services/supabase/storage.service';
import { useAuth } from '@/context/AuthContext';

interface AddBusinessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBusinessCreated: (business: Business) => void;
  existingBusiness?: Business | null;
}

export const AddBusinessDialog: React.FC<AddBusinessDialogProps> = ({
  open,
  onOpenChange,
  onBusinessCreated,
  existingBusiness,
}) => {
  const { firebaseUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(existingBusiness?.logo || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: existingBusiness?.name || '',
    tax_id: existingBusiness?.tax_id || '',
    logo: existingBusiness?.logo || '',
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Please upload an image file (PNG, JPG, GIF, WEBP, SVG)');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size too large. Maximum size is 2MB');
      return;
    }

    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData({ ...formData, logo: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser || !formData.name.trim()) return;

    setIsSaving(true);
    try {
      let logoUrl = formData.logo;

      // Upload logo if a new file was selected
      if (logoFile) {
        setIsUploadingLogo(true);
        logoUrl = await storageService.uploadLogo(logoFile, firebaseUser.uid, existingBusiness?.id);
        setIsUploadingLogo(false);
      }

      let business: Business;

      if (existingBusiness) {
        // Update existing business
        business = await businessService.updateBusiness(existingBusiness.id, {
          name: formData.name.trim(),
          tax_id: formData.tax_id.trim() || undefined,
          logo: logoUrl || undefined,
        });
      } else {
        // Create new business
        business = await businessService.createBusiness(firebaseUser.uid, {
          name: formData.name.trim(),
          tax_id: formData.tax_id.trim() || undefined,
          logo: logoUrl || undefined,
        });
      }

      onBusinessCreated(business);
      onOpenChange(false);

      // Reset form
      setFormData({ name: '', tax_id: '', logo: '' });
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      console.error('Error saving business:', error);
      alert(error instanceof Error ? error.message : 'Failed to save business. Please try again.');
    } finally {
      setIsSaving(false);
      setIsUploadingLogo(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {existingBusiness ? 'Edit Business' : 'Add Business'}
          </DialogTitle>
          <DialogDescription>
            {existingBusiness
              ? 'Update your business information.'
              : 'Add your business information to your profile.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Business Name */}
            <div className="space-y-2">
              <Label htmlFor="business_name">
                Business Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="business_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter business name"
                required
              />
            </div>

            {/* Tax ID */}
            <div className="space-y-2">
              <Label htmlFor="tax_id">Tax ID (Optional)</Label>
              <Input
                id="tax_id"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                placeholder="Enter tax ID"
              />
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <Label htmlFor="logo">Logo (Optional)</Label>

              {logoPreview ? (
                <div className="flex items-center gap-3">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="h-16 w-16 rounded object-cover border"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">
                      {logoFile ? logoFile.name : 'Current logo'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    ref={fileInputRef}
                    id="logo"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                    onChange={handleFileChange}
                    disabled={isSaving}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSaving}
                    className="gap-2 flex-1"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Logo
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Max file size: 2MB. Formats: PNG, JPG, GIF, WEBP, SVG
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !formData.name.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {isUploadingLogo ? 'Uploading...' : isSaving ? 'Saving...' : existingBusiness ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
